import * as React from 'react';
import { DoodleBodyKey, DoodleBodyParts } from './parts/body';
import { DoodleEyesKey, DoodleEyesParts } from './parts/eyes';
import { DoodleMouthKey, DoodleMouthParts } from './parts/mouth';
import { DoodleAccessoryKey, DoodleAccessoryParts } from './parts/accessories';
import styled, { keyframes } from 'styled-components';
import { useRecoilValue } from 'recoil';
import { AProfile } from '../../../atoms/global/users';

export const DEFAULT_DOODLE_CONFIG: DoodleConfig = {
	body: 'corps-1',
	eyes: 'yeux-1',
	mouth: 'bouche-1',
	bodyColor: 'white'
};

// keys are saved in doodle configurations: never rename or remove one
export const DoodleColors = {
	'white': '#FFFFFF',
	'purple-1': '#DE36DB',
	'purple-2': '#EBAEFF',
	'purple-3': '#F8E7FF',
	'blue-1': '#74BFFF',
	'blue-2': '#E1F2FF',
	'green-1': '#94F05A',
	'green-2': '#E7F9DD',
	'yellow-1': '#FEDA3F',
	'yellow-2': '#FFF4C3',
	'orange-1': '#FF7B15',
	'orange-2': '#FFE3C2',
	'red-1': '#FF5959',
	'red-2': '#FFDDD4',
	'pink-1': '#FF82C3',
	'pink-2': '#FFE8F8',
	'black-1': '#BABABA',
	'black-2': '#EDEDED',
	'sand-1': '#E6DDDD',
	'sand-2': '#F2EAE8',
	'sand-3': '#FCF5F0',
	'sand-4': '#FFF8F5'
};

export type DoodleColorKey = keyof typeof DoodleColors;

export type DoodleConfig = {
	body: DoodleBodyKey,
	eyes: DoodleEyesKey,
	mouth: DoodleMouthKey,
	accessory?: DoodleAccessoryKey,
	bodyColor: DoodleColorKey,
	color?: string
};

export type DoodleProps = {
	size?: number | string,
	style?: React.CSSProperties
	width?: number | string,
	height?: number | string,
	loading?: boolean
};

export function UserDoodle(props: DoodleProps) {
	const profile = useRecoilValue(AProfile);

	return <Doodle {...props} config={profile.doodle_configuration ?? DEFAULT_DOODLE_CONFIG} />;
}

// Every part is drawn on the same 240x240 frame, so they only need to be stacked
export default function Doodle({ config, size = 100, width, height, style, loading }: DoodleProps & { config: Partial<DoodleConfig> }) {
	const Body = DoodleBodyParts[config.body ?? DEFAULT_DOODLE_CONFIG.body];
	const Eyes = DoodleEyesParts[config.eyes ?? DEFAULT_DOODLE_CONFIG.eyes];
	const Mouth = DoodleMouthParts[config.mouth ?? DEFAULT_DOODLE_CONFIG.mouth];
	const Accessory = config.accessory ? DoodleAccessoryParts[config.accessory] : undefined;
	const layer: React.CSSProperties = { position: 'absolute', inset: 0 };

	return <DoodleWrapper width={width ?? size} height={height ?? size} loading={loading} style={{ ...style }}>
		<Body style={layer} size="100%" bodyColor={config.color ?? DoodleColors[config.bodyColor ?? DEFAULT_DOODLE_CONFIG.bodyColor] ?? DoodleColors.white} />
		<Mouth style={layer} size="100%" />
		<Eyes style={layer} size="100%" />
		{Accessory !== undefined && <Accessory style={layer} size="100%" />}
	</DoodleWrapper>;
}


const jumping = keyframes`
	0%  { transform: translateY(0)     scale(1, 1);                                            }
  	10% { transform: translateY(0)     scale(1.25, 0.7);  animation-timing-function: ease-out; } /* élan */
  	13% { transform: translateY(-10%)  scale(0.85, 1.2);                                       } /* décollage, étiré */
  	20% { transform: translateY(-30%)  scale(1, 1);       animation-timing-function: ease-in;  } /* sommet */
  	26% { transform: translateY(0)     scale(0.9, 1.1);                                        } /* arrivée au sol */
  	30% { transform: translateY(0)     scale(1.15, 0.85);                                      } /* écrasement d'atterrissage */
  	33% { transform: translateY(0)     scale(1, 1);                                            }
`;

const DoodleWrapper = styled.div<{ loading?: boolean, width: number | string, height: number | string }>`
	flex-shrink: 0;
	position: relative;
	width: ${p => `${p.width}${typeof p.width === 'number' ? 'px' : ''}`};
	height: ${p => `${p.height}${typeof p.height === 'number' ? 'px' : ''}`};
	${p => p.loading ? `animation: ${jumping} ease 2.5s infinite;` : ''}

`;
